<template>
  <section id="terms" class="adviser-section terms">
    <AdviserSectionHeading :title="terms.heading" variant="b" />
    <p v-if="terms.intro" class="terms-intro">{{ terms.intro }}</p>
    <ol class="terms-list">
      <li v-for="(item, index) in terms.items" :key="index">
        <TermText :text="item" />
      </li>
    </ol>
    <div
      v-for="section in terms.sections"
      :key="section.heading"
      class="terms-subsection"
    >
      <h2>{{ section.heading }}</h2>
      <ol class="terms-list">
        <li v-for="(item, index) in section.items" :key="index">
          <TermText :text="itemText(item)" />
          <ol v-if="item.items" class="terms-sublist">
            <li v-for="(subItem, subIndex) in item.items" :key="subIndex">
              <TermText :text="subItem" />
            </li>
          </ol>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
  import AdviserSectionHeading from './AdviserSectionHeading.vue';

  const EMAIL_PATTERN = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

  // Renders a term's text with any email address as a mailto link. The copy
  // is legal-approved plain text, so this splits it at render time instead
  // of putting markup into the constants (and avoids v-html).
  const TermText = {
    name: 'TermText',
    functional: true,
    props: {
      text: {
        type: String,
        required: true,
      },
    },
    render(h, { props }) {
      const parts = props.text
        .split(EMAIL_PATTERN)
        .map((part, index) =>
          index % 2 === 1
            ? h('a', { attrs: { href: `mailto:${part}` } }, part)
            : part
        );
      return h('span', parts);
    },
  };

  // Terms and Conditions block for the adviser channel pages. `terms` is the
  // structured object from constants/adviser-enrolment/platform-terms.js:
  // heading, intro, numbered items, then titled sub-sections whose items may
  // carry a nested numbered list. Spacing is measured from Figma: 30 under
  // each heading, 60 above each sub-heading, 4 between items.
  export default {
    name: 'AdviserTermsSection',
    components: {
      AdviserSectionHeading,
      TermText,
    },
    props: {
      terms: {
        type: Object,
        required: true,
      },
    },
    methods: {
      itemText(item) {
        return typeof item === 'string' ? item : item.text;
      },
    },
  };
</script>

<style scoped lang="scss">
  @import '../../../constants/styles/adviser-pages.scss';

  .terms {
    @include adviser-content-column;
    margin-top: $adviser-section-gap;
    // Keep the heading clear of the fixed app bar when #terms is scrolled to
    // (same offset as #get-registered in AdviserEnrolmentContent.vue).
    scroll-margin-top: 110px;
    padding-bottom: $adviser-footer-gap;

    .terms-intro {
      @include adviser-body;
      font-weight: 700;
      margin: 30px 0 24px;
    }

    .terms-list {
      @include adviser-body;
      margin: 0;
      padding-left: 20px;
      li {
        margin-bottom: 4px;
      }
    }
    a {
      color: $adviser-green-dark;
      text-decoration: underline;
    }
    .terms-sublist {
      @include adviser-body;
      list-style-type: lower-alpha;
      margin: 4px 0 0 0;
      padding-left: 20px;
    }

    .terms-subsection {
      margin-top: 60px;
      h2 {
        @include adviser-heading-b;
        margin-bottom: 30px;
      }
    }
  }
</style>
